import React, { useState } from 'react';
import AccountSearchInput from '../../components/AccountSearchInput';
import { getDivisionLabel } from '../../config/divisions';

const AddContactModal = ({ isOpen, onClose, onContactAdded, accounts = [] }) => {
  const [formData, setFormData] = useState({
    Name: '',
    AccountID: '',
    Designation: '',
    Email1: '',
    Email2: '',
    Phone1: '',
    Phone2: '',
    Notes: '',
    Country: ''
  });
  const [dupWarning, setDupWarning] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);



  const DOMAIN_COUNTRY = {
    'de':'Germany','tr':'Turkey','pl':'Poland','fr':'France','it':'Italy',
    'nl':'Netherlands','at':'Austria','be':'Belgium','ch':'Switzerland',
    'es':'Spain','pt':'Portugal','cz':'Czech Republic','sk':'Slovakia',
    'hu':'Hungary','ro':'Romania','dk':'Denmark','se':'Sweden','no':'Norway',
    'fi':'Finland','co.uk':'UK','uk':'UK','us':'USA','com.br':'Brazil',
    'com.ar':'Argentina','com.mx':'Mexico','ae':'UAE','sa':'Saudi Arabia',
    'co.za':'South Africa','com.au':'Australia','co.jp':'Japan','co.kr':'South Korea',
    'cn':'China','com.tw':'Taiwan','com.sg':'Singapore','com.my':'Malaysia',
    'co.id':'Indonesia','co.th':'Thailand','com.vn':'Vietnam',
    'in':'India','co.in':'India','com.pk':'Pakistan',
  };

  const detectCountryFromEmail = (email) => {
    if (!email || !email.includes('@')) return '';
    const domain = email.split('@')[1]?.toLowerCase() || '';
    for (const [ext, country] of Object.entries(DOMAIN_COUNTRY)) {
      if (domain.endsWith('.' + ext) || domain === ext) return country;
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };
    // Auto-detect country from email domain
    if (name === 'Email1' && value.includes('@')) {
      const detected = detectCountryFromEmail(value);
      if (detected && !formData.Country) {
        updates.Country = detected;
      }
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  };

  const checkDuplicateEmail = async (email) => {
    if (!email || !validateEmail(email)) return;
    try {
      const res = await fetch(`/api/v1/contacts/check-email?email=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.exists) {
        setDupWarning(`⚠️ Email already exists — ${data.contact_name} at ${data.company_name}`);
      } else {
        setDupWarning(null);
      }
    } catch {
      setDupWarning(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.Name.trim()) {
        throw new Error('Name is required');
      }
      if (!formData.AccountID) {
        throw new Error('Account is required');
      }
      if (!formData.Designation.trim()) {
        throw new Error('Designation is required');
      }
      if (!formData.Email1.trim()) {
        throw new Error('Primary email is required');
      }

      // Validate email formats
      if (!validateEmail(formData.Email1)) {
        throw new Error('Primary email format is invalid');
      }
      if (formData.Email2.trim() && !validateEmail(formData.Email2)) {
        throw new Error('Secondary email format is invalid');
      }
      // Validate phone formats
      if (formData.Phone1.trim() && !validatePhone(formData.Phone1)) {
        throw new Error('Phone 1 format is invalid — enter 7 to 15 digits');
      }
      if (formData.Phone2.trim() && !validatePhone(formData.Phone2)) {
        throw new Error('Phone 2 format is invalid — enter 7 to 15 digits');
      }

      // Create payload with required fields
      const payload = {
        Name: formData.Name.trim(),
        AccountID: parseInt(formData.AccountID),
        Designation: formData.Designation.trim(),
        Email1: formData.Email1.trim()
      };

      // Add optional fields only if they have values
      if (formData.Email2.trim()) {
        payload.Email2 = formData.Email2.trim();
      }
      if (formData.Phone1.trim()) {
        payload.Phone1 = formData.Phone1.trim();
      }
      if (formData.Phone2.trim()) {
        payload.Phone2 = formData.Phone2.trim();
      }
      if (formData.Notes.trim()) {
        payload.Notes = formData.Notes.trim();
      }
      if (formData.Country.trim()) {
        payload.Country = formData.Country.trim();
      }

      await onContactAdded(payload);
      
      // Reset form and close modal on success
      setFormData({
        Name: '',
        AccountID: '',
        Designation: '',
        Email1: '',
        Email2: '',
        Phone1: '',
        Phone2: '',
        Notes: '',
        Country: ''
      });
      setDupWarning(null);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create contact');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        Name: '',
        AccountID: '',
        Designation: '',
        Email1: '',
        Email2: '',
        Phone1: '',
        Phone2: '',
        Notes: '',
        Country: ''
      });
      setError(null);
      setDupWarning(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add New Contact</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Contact Information */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700">Contact Information</h3>
            
            <div>
              <label htmlFor="Name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                id="Name"
                name="Name"
                value={formData.Name}
                onChange={handleInputChange}
                required
                placeholder="Enter full name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account *
              </label>
              <AccountSearchInput
                value={formData.AccountID}
                onChange={(id, name) => setFormData(prev => ({ ...prev, AccountID: id || '', AccountName: name || '' }))}
                placeholder="Search company name..."
                required
                allowNew={false}
              />
              {!formData.AccountID && (
                <p className="mt-1 text-xs text-gray-400">Type at least 2 characters to search</p>
              )}
            </div>



            <div>
              <label htmlFor="Designation" className="block text-sm font-medium text-gray-700 mb-1">
                Designation *
              </label>
              <input
                type="text"
                id="Designation"
                name="Designation"
                value={formData.Designation}
                onChange={handleInputChange}
                required
                placeholder="Enter job title or designation"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700">Contact Details</h3>
            
            <div>
              <label htmlFor="Email1" className="block text-sm font-medium text-gray-700 mb-1">
                Email 1 *
              </label>
              <input
                type="email"
                id="Email1"
                name="Email1"
                value={formData.Email1}
                onChange={handleInputChange}
                onBlur={(e) => checkDuplicateEmail(e.target.value)}
                required
                placeholder="primary@email.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {dupWarning && (
                <p className="mt-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">{dupWarning}</p>
              )}
            </div>

            <div>
              <label htmlFor="Email2" className="block text-sm font-medium text-gray-700 mb-1">
                Email 2
              </label>
              <input
                type="email"
                id="Email2"
                name="Email2"
                value={formData.Email2}
                onChange={handleInputChange}
                placeholder="secondary@email.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="Phone1" className="block text-sm font-medium text-gray-700 mb-1">
                Phone 1
              </label>
              <input
                type="tel"
                id="Phone1"
                name="Phone1"
                value={formData.Phone1}
                onChange={handleInputChange}
                placeholder="9876543210"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="Phone2" className="block text-sm font-medium text-gray-700 mb-1">
                Phone 2
              </label>
              <input
                type="tel"
                id="Phone2"
                name="Phone2"
                value={formData.Phone2}
                onChange={handleInputChange}
                placeholder="9876543210"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700">Additional Information</h3>

            <div>
              <label htmlFor="Country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                id="Country"
                name="Country"
                value={formData.Country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select country</option>
                {["India","Germany","Turkey","Poland","France","Italy","Netherlands","Austria","Belgium","Switzerland","Spain","Portugal","Czech Republic","Slovakia","Hungary","Romania","Bulgaria","Croatia","Slovenia","Denmark","Sweden","Norway","Finland","UK","USA","Canada","Brazil","Argentina","Mexico","UAE","Saudi Arabia","Kuwait","Qatar","Oman","Bahrain","Israel","South Africa","Australia","New Zealand","Japan","South Korea","China","Taiwan","Singapore","Malaysia","Indonesia","Thailand","Vietnam","Philippines","Bangladesh","Pakistan","Sri Lanka","Nepal","Egypt","Morocco","Tunisia","Algeria","Nigeria","Kenya","Ghana"].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="Notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes/Comments
              </label>
              <textarea
                id="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any additional notes or comments"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Adding...' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal;
